import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBadge,
  faBell,
  faBook,
  faBookOpen,
  faBurst,
  faChartColumn,
  faChartLine,
  faChartMixed,
  faChartSimple,
  faCircle,
  faClipboardList,
  faComment,
  faCommentCheck,
  faCommentSms,
  faComments,
  faEllipsis,
  faFileLines,
  faFilePen,
  faFire,
  faFlag,
  faFlask,
  faGaugeHigh,
  faGear,
  faHeadSideGear,
  faHouse,
  faImage,
  faInputPipe,
  faLink,
  faList,
  faListCheck,
  faListTimeline,
  faMagnifyingGlassChart,
  faMessages,
  faMicrophone,
  faNewspaper,
  faPenRuler,
  faPhone,
  faRobot,
  faScrewdriverWrench,
  faShieldHalved,
  faSitemap,
  faSparkles,
  faSquare,
  faStar,
  faSwatchbook,
  faTable,
  faTableColumns,
  faTag,
  faUser,
  faUsers,
  faWindow,
} from '@fortawesome/pro-regular-svg-icons';
import { faHeadset } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FunctionComponent, ReactNode, SVGProps } from 'react';
import AssistantSvg from '../assets/images/assistant.svg?react';
import KarteSvg from '../assets/images/icon_karte.svg?react';
// L2 メニュー用 SVG（navigation-v5 の f-*.svg をコピー）
import BehaviorChainSvg from '../assets/images/product/f-behavior_chain.svg?react';
import DashboardSvg from '../assets/images/product/f-dashboard.svg?react';
import EventSvg from '../assets/images/product/f-event.svg?react';
import FunnelSvg from '../assets/images/product/f-funnel.svg?react';
import GoalSvg from '../assets/images/product/f-goal.svg?react';
import InsightDimensionSvg from '../assets/images/product/f-insight_dimension.svg?react';
import IssuesAnswerSvg from '../assets/images/product/f-issues_answer.svg?react';
import IssuesConnectBoardSvg from '../assets/images/product/f-issues_connect_board.svg?react';
import IssuesScenarioSvg from '../assets/images/product/f-issues_scenario.svg?react';
import IssuesSupportActionSvg from '../assets/images/product/f-issues_support_action.svg?react';
import IssuesSupportSiteSvg from '../assets/images/product/f-issues_support_site.svg?react';
import IssuesWidgetSvg from '../assets/images/product/f-issues_support_widget.svg?react';
import ReportSvg from '../assets/images/product/f-report.svg?react';
import SegmentSvg from '../assets/images/product/f-segment.svg?react';
import SettingSvg from '../assets/images/product/f-setting.svg?react';
import UserListSvg from '../assets/images/product/f-user_list.svg?react';
import UserRatioSvg from '../assets/images/product/f-user_ratio.svg?react';
import UsertypeAnalysisSvg from '../assets/images/product/f-usertype_analysis.svg?react';
import AiOperatorSvg from '../assets/images/product/p-ai-operator.svg?react';
import CraftSvg from '../assets/images/product/p-craft.svg?react';
import DatahubSvg from '../assets/images/product/p-datahub.svg?react';
import IssuesSvg from '../assets/images/product/p-issues.svg?react';
import KnowledgeSvg from '../assets/images/product/p-knowledge.svg?react';
import KnowledgeDeskSvg from '../assets/images/product/p-knowledge-desk.svg?react';
import QantConnectSvg from '../assets/images/product/p-qant-connect.svg?react';
import QantDebesoSvg from '../assets/images/product/p-qant-debeso.svg?react';
import QantFaqSvg from '../assets/images/product/p-qant-faq.svg?react';
import QantSpeakSvg from '../assets/images/product/p-qant-speak.svg?react';
import QantTabulaSvg from '../assets/images/product/p-qant-tabula.svg?react';
import QantVocSvg from '../assets/images/product/p-qant-voc.svg?react';
import QantWebSvg from '../assets/images/product/p-qant-web.svg?react';
import QualtdataSvg from '../assets/images/product/p-qualtdata.svg?react';

function l1Logo(Svg: FunctionComponent<SVGProps<SVGSVGElement>>): (props: SVGProps<SVGSVGElement>) => ReactNode {
  return (props) => <Svg aria-hidden="true" {...props} />;
}

/**
 * L1 (プロダクト) のブランドロゴ。
 * アセットは navigation-v5 の `src/assets/images/product/` および `icon_karte.svg` をコピー。
 *
 * navigation-v5 の ProductIcon 対応（調査メモ）:
 * - Issues / RightSupport: `p-issues.svg` → `qantIssues`, `qantConnect`
 * - Insight（intelligence 相当）: `p-insight.svg` → `qantInsight`（QANT では `p-qant-web.svg` を充当）
 * - VoC: `p-voc.svg` → `qantVoc`（`p-qant-voc.svg`）
 * - Speak: `p-speak.svg` → `qantSpeak`（`p-qant-speak.svg`）
 *
 * その他:
 * - qantWebAgent → p-craft.svg（暫定）
 * - qantFaq → p-message.svg
 * - qantAiOperator → p-ai-operator.svg
 * - qantKnowledge → p-knowledge.svg
 * - qantKnowledgeDesk → p-knowledge-desk.svg
 * - qantQualityReview → p-qualtdata.svg
 * - qantSupportDataHub → p-datahub.svg
 * - qantTabula → p-qant-tabula.svg
 * - qantDebeso → p-qant-debeso.svg
 * - qantKarte → icon_karte.svg
 */
const L1_LOGO_MAP: { [key: string]: (props: SVGProps<SVGSVGElement>) => ReactNode } = {
  qantWeb: l1Logo(QantWebSvg),
  qantIssues: l1Logo(IssuesSvg),
  /** navigation-v5 の KARTE Insight（L1）に相当。QANT 製品アイコンは p-qant-web を使用 */
  qantInsight: l1Logo(QantWebSvg),
  qantWebAgent: l1Logo(CraftSvg),
  qantFaq: l1Logo(QantFaqSvg),
  qantConnect: l1Logo(QantConnectSvg),
  qantDebeso: l1Logo(QantDebesoSvg),
  qantSpeak: l1Logo(QantSpeakSvg),
  qantAiOperator: l1Logo(AiOperatorSvg),
  qantVoc: l1Logo(QantVocSvg),
  qantKnowledge: l1Logo(KnowledgeSvg),
  qantKnowledgeDesk: l1Logo(KnowledgeDeskSvg),
  qantQualityReview: l1Logo(QualtdataSvg),
  qantSupportDataHub: l1Logo(DatahubSvg),
  qantTabula: l1Logo(QantTabulaSvg),
  qantKarte: l1Logo(KarteSvg),
};

type L2SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

/**
 * L2 メニューアイコン (SVG)。
 * navigation-v5 の `ProductMenuIcon` の SVG マッピングに準拠し、`f-*.svg` を直接読み込む。
 *
 * - Insight: userList / userRatio / usertypeAnalysis / behaviorChain /
 *   insightDimension / goal / funnel / report
 * - Issues / Speak: issuesDashboard (= f-dashboard) / issuesReport (= f-report) /
 *   issuesAction / issuesSupportSite / issuesScenario / issuesFaq / issuesConnectBoard /
 *   issuesWidget (= f-issues_support_widget, 問い合わせ窓口)
 * - Insight / KARTE: event (= f-event) / segment (= f-segment)
 * - 共通: setting (VoC / Speak の設定)
 */
const L2_SVG_MAP: { [key: string]: L2SvgComponent } = {
  neuroAiAssistant: AssistantSvg,
  setting: SettingSvg,
  issuesDashboard: DashboardSvg,
  issuesReport: ReportSvg,
  issuesAction: IssuesSupportActionSvg,
  issuesSupportSite: IssuesSupportSiteSvg,
  issuesScenario: IssuesScenarioSvg,
  issuesFaq: IssuesAnswerSvg,
  issuesConnectBoard: IssuesConnectBoardSvg,
  issuesWidget: IssuesWidgetSvg,
  event: EventSvg,
  segment: SegmentSvg,
  userList: UserListSvg,
  userRatio: UserRatioSvg,
  usertypeAnalysis: UsertypeAnalysisSvg,
  behaviorChain: BehaviorChainSvg,
  insightDimension: InsightDimensionSvg,
  goal: GoalSvg,
  funnel: FunnelSvg,
  report: ReportSvg,
};

/**
 * L2 メニューアイコン (Font Awesome)。
 * navigation-v5 の `ProductMenuFontAwesomeIcons` 準拠。
 *
 * - `insightCustomReport` / `vocAnalytics` → faChartMixed (pro-regular)
 * - `vocDashboard` → faNewspaper (pro-regular)
 * - `vocTopics` → faCommentCheck (pro-regular)
 * - `vocIssues` → faListCheck (pro-regular)
 * - `vocUserGuides` → faBookOpen (pro-regular)
 * - `event` / `segment` → f-event.svg / f-segment.svg (Insight と同じ SVG。FA フォールバックは faFire / faUsers)
 * - `reasons` → faBadge (pro-regular) — QANT グロナビ Figma 準拠
 * - `checkpoints` → faFlag (pro-regular)
 * - `form` → faInputPipe (pro-regular)
 * - `issuesAgent` → faMessages (pro-regular) — navigation-v5 ProductMenuFontAwesomeIcons 準拠
 * - `burst` → faBurst (pro-regular) — Speak の「課題」。v5 は icon 名 `burst` を直接 FA に引いていた
 * - `smartFaqSiteSettings` → faWindow / `smartFaqSiteDesign` → faPenRuler /
 *   `smartFaqSurveyResponses` → faFilePen (pro-regular) — スマートFAQ 新デザイン Figma 準拠
 *
 * その他、navigation-v5 では未マップだが本パッケージで補助的に使うキー
 * (knowledge / agent 系など) は近似 FA で残す。
 */
const L2_ICON_MAP: { [key: string]: IconDefinition } = {
  insightCustomReport: faChartMixed,
  vocDashboard: faNewspaper,
  vocAnalytics: faChartMixed,
  vocTopics: faCommentCheck,
  vocIssues: faListCheck,
  vocUserGuides: faBookOpen,
  event: faFire,
  segment: faUsers,
  reasons: faBadge,
  checkpoints: faFlag,
  form: faInputPipe,
  issuesAgent: faMessages,
  burst: faBurst,
  brandSettings: faSwatchbook,
  smsSettings: faCommentSms,
  reasonTags: faTag,
  tool: faScrewdriverWrench,
  smartBanner: faImage,
  agentTest: faFlask,
  // 補助 (navigation-v5 ではアイコン未指定のメニュー向けの近似)
  vocReview: faStar,
  vocReport: faChartColumn,
  issuesLibrary: faBook,
  knowledgeLibrary: faBook,
  qualityReview: faShieldHalved,
  customerData: faUsers,
  resourceTable: faTable,
  other: faEllipsis,
  knowledge: faBookOpen,
  agent: faRobot,
  agentLog: faFileLines,
  settings: faGear,
  review: faStar,
  serviceLog: faFileLines,
  list: faList,
  campaign: faComments,
  outbound: faPhone,
  jobFlow: faSitemap,
  integration: faLink,
  user: faUser,
  aiOperatorAgent: faRobot,
  aiOperatorAgentLog: faFileLines,
  knowledgeDeskLibrary: faBook,
  knowledgeDeskResponseBoard: faHeadset,
  knowledgeDeskAfterCallWork: faClipboardList,
  knowledgeDeskSpeechRecognition: faMicrophone,
  knowledgeDeskNotification: faBell,
  knowledgeDeskReport: faChartMixed,
  knowledgeDeskKnowledge: faBookOpen,
  // QANT 各アプリ現状ナビ忠実版で使用するキー（実コードの FontAwesome アイコンに対応）
  // Knowledge Hub
  docImprovement: faSparkles,
  documentCoverage: faMagnifyingGlassChart,
  knowledgeComment: faComment,
  // Neuro (AIオペレーター)
  neuroReport: faChartLine,
  neuroLatency: faGaugeHigh,
  neuroMetrics: faChartSimple,
  neuroIssues: faBurst,
  neuroE2eTest: faComments,
  neuroTestCases: faFlask,
  neuroTestResults: faClipboardList,
  neuroConversations: faListTimeline,
  neuroAgentSettings: faHeadSideGear,
  tableDefinition: faTableColumns,
  taskList: faListCheck,
  // スマートFAQ
  smartFaqSiteSettings: faWindow,
  smartFaqSiteDesign: faPenRuler,
  smartFaqSurveyResponses: faFilePen,
  // 共通
  notification: faBell,
  home: faHouse,
  // 旧キー互換（icons.tsx 内部のフォールバック用）
  speakPhone: faPhone,
  speakFlow: faSitemap,
  speakReport: faChartColumn,
  speakLibrary: faBook,
  speakDashboard: faSquare,
};

/**
 * L1 (プロダクト) アイコン名からアイコン要素を返す。
 * 未マップの場合は塗り潰し円のフォールバック。
 */
export function L1Icon({ icon, className }: { icon?: string; className?: string }): ReactNode {
  const Logo = icon ? L1_LOGO_MAP[icon] : undefined;
  if (Logo) {
    return <Logo className={className} width="1em" height="1em" />;
  }
  return <FontAwesomeIcon icon={faCircle} className={className} />;
}

/**
 * L2 (メニュー) アイコン名からアイコン要素を返す。
 * SVG マッピング → Font Awesome マッピング → 四角のフォールバックの順で解決する。
 */
export function L2Icon({ icon, className }: { icon?: string; className?: string }): ReactNode {
  const Svg = icon ? L2_SVG_MAP[icon] : undefined;
  if (Svg) {
    return <Svg aria-hidden="true" className={className} width="1em" height="1em" fill="currentColor" />;
  }
  const def = (icon ? L2_ICON_MAP[icon] : undefined) ?? faSquare;
  return <FontAwesomeIcon icon={def} className={className} />;
}
